/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    AddressResource,
    AiActivitiesType,
    PageQueryParams,
    PeriodQueryParams,
    SearchQueryParams,
} from '../apiCommon.typings';
import { DocType } from '../documents/documentsClient.typings';
import { StoreType, storeStatus, IntegrationLevel } from '../stores/storesClientTypings';
import { companiesStatus } from '../companies/companiesClient.typings';
import { Viewport } from '../../../components/Map/typings';
import {
    SoftwareVersionItemDto,
    SoftwareVersionStatus,
    VersionStatus,
} from '../cashRegisters/cashRegistersClient.typings';

export interface CashBoxDetailsDto {
    taxNumber: string;
    cashBoxType: CashBoxTypes;
    companyTaxNumber: string;
    companyName: string;
    storeTaxNumber: number;
    operatorTaxNumber: number;
    operatorName: string;
    storeName: string;
    storeType: string;
    storeAddress: AddressResource;
    storeFullAddress: string;
    online: boolean;
    validTime: boolean;
    status: CashBoxesStatus;
    firmwareVersion: string;
    temporary: boolean;
    timeDelta?: number;
    taxationTypes: string[];
    storeTaxAuthority: string;
    server: boolean;
    statusComment: string;
    statusDateAtUtc: string;
    blockEndAtUtc: string;
    blockEndComment: string;
    blockStartAtUtc: string;
    blockStartComment: string;
    cashRegisterModelName: string;
    cashRegisterModelTaxNumber: number;
    cashRegisterFactoryNumber: string;
    fiscalDriverVersion: string;
    storeStatus: storeStatus;
    companyStatus: companiesStatus;
    os: string;
    pingDateAtUtc: string;
    deviceFactoryNumber: string;
    certificateEndDateAtUtc: string;
    registeredDateAtUtc: string;
    auditLastDateAtUtc: string;
    softwareVersion?: string;
    softwareVersionStatus?: VersionStatus;
    portable?: boolean;
    classification?: AiActivitiesType;
    classificationProbability?: number;
    posIntegration: IntegrationsCashboxStatuses;
    erpIntegration: IntegrationsCashboxStatuses;
    integrationLevel: IntegrationLevel;
    isStoreErpSystem: boolean;
    storeWebSite?: string;
    currentError?: CashBoxErrorData;
    storeMainActivity?: string;
    storeMiddleActivity?: string;
    storeLowerActivity?: string;
}

export interface CashBoxErrorData {
    type: CashBoxErrorTypes;
    insertedAtUtc: string;
    updatedAtUtc: string;
    operation: CashBoxOperationErrors;
    category: CashBoxErrorCategories;
    description: string;
}

export interface CashBoxesIntegrationStatusesParams {
    cashBoxTaxNumber: string;
    posIntegration?: IntegrationsCashboxStatuses;
    erpIntegration?: IntegrationsCashboxStatuses;
}

export interface CashBoxListItemDto {
    taxNumber: string;
    companyTaxNumber: string;
    storeTaxNumber: string;
    storeName: string;
    storeType: StoreType;
    storeAddress: AddressResource;
    storeFullAddress: string;
    storeTaxAuthority: string;
    online: boolean;
    validTime: boolean;
    status: CashBoxesStatus;
    firmwareVersion: string;
    fiscalDriverVersion: string;
    temporary: boolean;
    timeDelta?: number;
    taxationTypes: string[];
    server: boolean;
    cashRegisterFactoryNumber: string;
    cashBoxType: CashBoxTypes;
    posIntegration?: IntegrationsCashboxStatuses;
    erpIntegration?: IntegrationsCashboxStatuses;
    storeMainActivity?: string;
    storeMiddleActivity?: string;
    storeLowerActivity?: string;
}

export interface CashBoxesListDto {
    afterKeyTaxNumber: string;
    cashBoxes: CashBoxListItemDto[];
}

export interface CashBoxesListPageQueryParams {
    afterKeyTaxNumber: string;
    limit?: number;
}

export interface CashBoxesListFilterParams extends Partial<SearchQueryParams> {
    state: CashBoxeState;
    temporary?: boolean;
    server?: boolean;
    cashBoxTypes?: CashBoxTypes[];
    gpsTracking?: boolean;
    portable?: boolean;
    posIntegration?: IntegrationsCashboxStatuses[];
    erpIntegration?: IntegrationsCashboxStatuses[];
    cashBoxStatuses?: CashBoxesStatus[];
    softwareVersionStatuses?: SoftwareVersionStatus[];
    softwareVersions?: SoftwareVersionItemDto[];
    errorTypes?: CashBoxErrorTypes[];
    errorCategories?: CashBoxErrorCategories[];

    fromRegisteredAtUtc?: string;
    toRegisteredAtUtc?: string;
    fromOffline?: number | string;
    toOffline?: number | string;
    fromLastDocument?: number | string;
    toLastDocument?: number | string;
    fromExpiredCert?: number | string;
    toExpiredCert?: number | string;

    fromTimeDelta?: number | string;
    toTimeDelta?: number | string;

    cashRegisterModelTaxNumbers?: string[];
    operatorTaxNumbers?: string[];
    firmwareVersions?: string[];
    fiscalDriverVersions?: string[];
    taxationTypes?: string[];
    storeTaxAuthorityIds?: string[];
    regionCodes: string[];

    classifications?: AiActivitiesType[];
    disableProbability?: boolean;
    toClassificationPercent?: number;
    fromClassificationPercent?: number;

    storeTypes?: string[];
    storeMainActivities?: string[];
    storeMiddleActivities?: string[];
    storeLowerActivities?: string[];
}

export type CashBoxesListRequestParams = CashBoxesListPageQueryParams & CashBoxesListFilterParams;
export type CashBoxesListExcelRequestParams = CashBoxesListFilterParams;

export enum CashBoxeState {
    ONLINE = 'ONLINE',
    OFFLINE = 'OFFLINE',
}

export enum IntegrationsCashboxStatuses {
    INTEGRATED = 'INTEGRATED',
    NOT_INTEGRATED = 'NOT_INTEGRATED',
    NO_DATA = 'NO_DATA',
}

export enum ReplacementType {
    PRIMARY = 'PRIMARY',
    TEMPORARY = 'TEMPORARY',
}

export enum CashBoxTypes {
    PHYSICAL = 'PHYSICAL',
    SERVER = 'SERVER',
    VIRTUAL = 'VIRTUAL',
}

export const CASH_BOXES_STATUSES = [
    'REGISTRATION',
    'ACTIVE',
    'BLOCKED',
    'RESET',
    'BROKEN',
    'SERVICE',
    'ELIMINATED',
] as const;

export type CashBoxesStatus = (typeof CASH_BOXES_STATUSES)[number];

export enum CashBoxErrorTypes {
    SERVER_EXPIRED_CERTIFICATE = 'SERVER_EXPIRED_CERTIFICATE',
    SERVER_INVALID_SIGNATURE = 'SERVER_INVALID_SIGNATURE',
    SERVER_INVALID_ORDER = 'SERVER_INVALID_ORDER',
    SERVER_INVALID_CASHBOX_STATE = 'SERVER_INVALID_CASHBOX_STATE',
    SERVER_INVALID_CERTIFICATE = 'SERVER_INVALID_CERTIFICATE',
    SERVER_INVALID_FD_REQUEST = 'SERVER_INVALID_FD_REQUEST',
    SERVER_INVALID_DOCUMENT = 'SERVER_INVALID_DOCUMENT',
    FW_EXPIRED_CERTIFICATE = 'FW_EXPIRED_CERTIFICATE',
    FW_DOCS_ERROR = 'FW_DOCS_ERROR',
    FW_INVALID_CASHBOX_STATE = 'FW_INVALID_CASHBOX_STATE',
    FW_LOCK_ERROR = 'FW_LOCK_ERROR',
    FW_FILE_SYSTEM_ERROR = 'FW_FILE_SYSTEM_ERROR',
    FW_INVALID_CERTIFICATE = 'FW_INVALID_CERTIFICATE',
    FW_KEY_ERROR = 'FW_KEY_ERROR',
    FW_INITIALIZATION_ERROR = 'FW_INITIALIZATION_ERROR',
    FW_MEMORY_ERROR = 'FW_MEMORY_ERROR',
    FW_HARDWARE_ERROR = 'FW_HARDWARE_ERROR',
    FW_PROTOCOL_ERROR = 'FW_PROTOCOL_ERROR',
}

export enum CashBoxErrorCategories {
    CERTIFICATE = 'CERTIFICATE',
    SIGNATURE = 'SIGNATURE',
    DOCUMENT = 'DOCUMENT',
    CASHBOX_STATE = 'CASHBOX_STATE',
    FILESYSTEM = 'FILESYSTEM',
    HARDWARE = 'HARDWARE',
    OTHERS = 'OTHERS',
}

export const CASH_BOX_ERROR_TYPES_BY_CATEGORY: {
    [key in CashBoxErrorCategories]: CashBoxErrorTypes[];
} = {
    CERTIFICATE: [CashBoxErrorTypes.SERVER_EXPIRED_CERTIFICATE, CashBoxErrorTypes.FW_EXPIRED_CERTIFICATE],
    SIGNATURE: [CashBoxErrorTypes.SERVER_INVALID_SIGNATURE],
    DOCUMENT: [CashBoxErrorTypes.SERVER_INVALID_ORDER, CashBoxErrorTypes.FW_DOCS_ERROR],
    CASHBOX_STATE: [
        CashBoxErrorTypes.SERVER_INVALID_CASHBOX_STATE,
        CashBoxErrorTypes.FW_INVALID_CASHBOX_STATE,
        CashBoxErrorTypes.FW_LOCK_ERROR,
    ],
    FILESYSTEM: [
        CashBoxErrorTypes.FW_FILE_SYSTEM_ERROR,
        CashBoxErrorTypes.FW_INVALID_CERTIFICATE,
        CashBoxErrorTypes.FW_KEY_ERROR,
    ],
    HARDWARE: [
        CashBoxErrorTypes.FW_INITIALIZATION_ERROR,
        CashBoxErrorTypes.FW_MEMORY_ERROR,
        CashBoxErrorTypes.FW_HARDWARE_ERROR,
    ],
    OTHERS: [
        CashBoxErrorTypes.SERVER_INVALID_CERTIFICATE,
        CashBoxErrorTypes.SERVER_INVALID_FD_REQUEST,
        CashBoxErrorTypes.SERVER_INVALID_DOCUMENT,
        CashBoxErrorTypes.FW_PROTOCOL_ERROR,
    ],
};

export enum CashBoxOperationErrors {
    SERVER_PING = 'SERVER_PING',
    SERVER_REMOVE_DOC = 'SERVER_REMOVE_DOC',
    FW_GET_FACTORY_NUMBER = 'FW_GET_FACTORY_NUMBER',
    FW_GET_FACTORY_OPEN_KEY = 'FW_GET_FACTORY_OPEN_KEY',
    FW_GET_INFO = 'FW_GET_INFO',
    FW_GET_DOCUMENT_COUNT = 'FW_GET_DOCUMENT_COUNT',
    FW_GET_DOCUMENT_TO_SENT = 'FW_GET_DOCUMENT_TO_SENT',
    FW_GET_COMPANY_CERTIFICATE = 'FW_GET_COMPANY_CERTIFICATE',
    FW_OPEN_SHIFT = 'FW_OPEN_SHIFT',
    FW_CLOSE_SHIFT = 'FW_CLOSE_SHIFT',
    FW_SHIFT_STATUS = 'FW_SHIFT_STATUS',
    FW_CREATE_DOCUMENT = 'FW_CREATE_DOCUMENT',
    FW_PING_TICKET = 'FW_PING_TICKET',
}

export interface ChangeCashBoxStatusParams {
    status: CashBoxesStatus;
    comment: string;
}

export interface BlockCashBoxQueryParams {
    comment: string;
    endDate?: string;
    startDate?: string;
}

export interface UnblockCashBoxQueryParams {
    comment: string;
    startDate?: string;
}

// -----Documents history----- //

export type CashBoxDocumentsListFilterParams = {
    docType?: string;
    dateFrom: string;
    dateTo: string;
};

export type LoadCashBoxDocsFileParams = {
    docType: string;
    from_utc: string;
    to_utc: string;
    taxNumber: string;
};

export interface CashBoxLastDocument {
    docTypeId: DocType;
    createdAtUtc: string;
    insertedDateAtUtc: string;
    sum: number;
    docNumber: number; //z-report, openShift = reportNumber
    globalDocNumber: number;
}

// -----Statuses history----- //

export type CashBoxStatusesHistoryQueryParams = PageQueryParams;

export interface CashBoxStatusDto {
    status: CashBoxesStatus;
    dateAtUtc: string;
    duration: number;
    userName?: string;
    comment?: string;
}

export interface CashBoxStatusesHistoryDto {
    count: number;
    statuses: CashBoxStatusDto[];
}

// -----TimeDelta history----- //

export type CashBoxTimeDeltaHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface CashBoxTimeDeltaDto {
    validTime: boolean;
    dateAtUtc: string;
    timeDelta: number | null;
    duration: number | null;
}

export interface CashBoxTimeDeltaHistoryDto {
    count: number;
    timeDeltas: CashBoxTimeDeltaDto[];
}

// -----State history----- //

export type CashBoxStateHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface CashBoxStateDto {
    state: 'ONLINE' | 'OFFLINE';
    dateAtUtc: string;
    duration: number;
}

export interface CashBoxStateHistoryDto {
    count: number;
    states: CashBoxStateDto[];
}

//-----Audit history-----//

export type CashBoxAuditHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface AuditDto {
    status: 'SUCCESS' | 'FAILED';
    date: string;
    details: string;
}

export interface AuditBaseDto {
    year: number;
    quarter: number;
}

export interface CashBoxAuditHistoryItemDto extends AuditBaseDto {
    audits: AuditDto[];
}
export interface CashBoxAuditHistoryDto {
    count: number;
    audits: CashBoxAuditHistoryItemDto[];
}

//-----Replacement history-----//

export interface CashBoxReplacementDto {
    taxNumber: string;
    cashRegisterFactoryNumber: string;
    status: CashBoxesStatus;
    cashRegisterModelName: string;
    registrationDateAtUtc: string;
    temporary: boolean;
    certificateExpirationDateAtUtc: string;
}

export interface CashBoxReplacementHistoryDto {
    parents: CashBoxReplacementDto[];
    children: CashBoxReplacementDto[];
}

//-----Alerts history-----//

export type CashBoxAlertsHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export type CashBoxAlertsHistoryExcelParams = {
    month: string;
};

export interface CashBoxAlertsDto {
    month: string;
    minor: number;
    major: number;
    normal: number;
    count: number;
}

export interface CashBoxAlertsHistoryDto {
    count: number;
    alerts: CashBoxAlertsDto[];
}

// -----Cashboxes map----- // //TODO: move to map data typing

export interface CashBoxesMapFilterParams extends SearchQueryParams {
    afterKeyId?: string;
    limit?: number;
    cashBoxStatuses?: CashBoxesStatus[];
}

export interface CashBoxesMapSearchParams {
    viewport: Viewport;
    filterParams: CashBoxesMapFilterParams;
}

export interface Claster {
    count: number;
    geoHash: string;
}

export interface CashBoxPoint {
    taxNumber: string;
    lat: number;
    lon: number;
}

export interface CashBoxesMap {
    afterKeyId?: string;
    clusters: Claster[];
    points: CashBoxPoint[];
}

export interface CashBoxDetailsForMap {
    taxNumber: string;
    online: boolean;
    gpsUpdatedDateAtUtc: string;
}

//-----GPS tracking tab-----//

export interface CashboxLocationData {
    lat: number;
    lon: number;
    gpsUpdatedAtUtc: string;
    storeLat: number;
    storeLon: number;
}

export interface LocationsListItem {
    lat: number;
    lon: number;
    insertedDateAtUtc: string;
}

export interface HistoryLocationsList {
    count: number;
    items: LocationsListItem[];
}

export interface GpsTrackingLocationsList {
    accuracy: number;
    items: LocationsListItem[];
}

export interface HistoryLocationsFilterParams {
    fromInsertedDateAtUtc: string;
    toInsertedDateAtUtc: string;
}

export type HistoryLocationsQueryParams = HistoryLocationsFilterParams & PageQueryParams;

//-----Checksums history-----//

export interface CheckSumsHistoryItemDto {
    softwareVersion: string;
    softwareVersionStatus: VersionStatus;
    insertedDateAtUtc: string;
    updatedDateAtUtc: string;
    fiscalDriverVersion: string;
    firmwareVersion: string;
    checksum: string;
}

export interface CheckSumsHistoryDto {
    count: number;
    items: CheckSumsHistoryItemDto[];
}

//-----Errors history-----//

export type CashBoxErrorsHistoryQueryParams = PeriodQueryParams & PageQueryParams;

export interface CashBoxErrorsHistoryItemDto {
    id: number;
    type: CashBoxErrorTypes;
    category: CashBoxErrorCategories;
    insertedAtUtc: string;
    updatedAtUtc: string;
    active: boolean;
    operation: CashBoxOperationErrors;
    description: string;
}

export interface CashBoxErrorsHistoryDto {
    count: number;
    items: CashBoxErrorsHistoryItemDto[];
}

//-----Cashboxes statistics-----//

export interface CashBoxesStatsStates {
    active: number;
    blocked: number;
    inactive: number;
    notBlocked: number;
    offline: number;
    online: number;

    [key: string]: number;
}

export interface CashBoxesStatsStatuses {
    registration: number;
    active: number;
    blocked: number;
    reset: number;
    broken: number;
    service: number;
    eliminated: number;

    [key: string]: number;
}

export interface CashBoxesStatsTypes {
    temporary: number;
    primary: number;

    [key: string]: number;
}

export interface CashBoxesStatsTimer {
    correct: number;
    incorrect: number;

    [key: string]: number;
}

export interface CashBoxesStatsCertificates {
    valid: number;
    expired: number;

    [key: string]: number;
}

export interface CashBoxesStatsErrors {
    certificate: number;
    signature: number;
    document: number;
    cashboxState: number;
    filesystem: number;
    hardware: number;
    others: number;

    [key: string]: number;
}

export interface CashBoxesStats {
    states: CashBoxesStatsStates;
    statuses: CashBoxesStatsStatuses;
    types: CashBoxesStatsTypes;
    timers: CashBoxesStatsTimer;

    [key: string]:
        | CashBoxesStatsStates
        | CashBoxesStatsStatuses
        | CashBoxesStatsTypes
        | CashBoxesStatsTimer
        | CashBoxesStatsCertificates
        | CashBoxesStatsErrors;
}

export interface OperatorCashBoxesStats extends CashBoxesStats {
    certificates: CashBoxesStatsCertificates;
    errors: CashBoxesStatsErrors;
}
